import { create } from "zustand";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { getErrorMessage, isAbortError } from "@/lib/errors";
import {
  DEFAULT_BIN_MINUTES,
  type BinMinutes,
} from "@/features/analytics/utils/providerWorkingHours";
import type {
  AnalyticsCapacityDateField,
  Radiologist,
  RadiologistCapacityRecalcOut,
  RadiologistPatch,
} from "@/features/radiologists/types/radiologist";

function normalizeRadiologist(r: Radiologist): Radiologist {
  return {
    ...r,
    enabled_site_ids: r.enabled_site_ids ?? [],
    specialties: r.specialties ?? [],
  };
}

type RadiologistsState = {
  radiologists: Record<number, Radiologist>;
  loading: boolean;
  error?: string;
};

type RadiologistsActions = {
  list: (signal?: AbortSignal) => Promise<void>;
  get: (id: number, signal?: AbortSignal) => Promise<Radiologist | undefined>;
  create: (payload: Partial<Radiologist>) => Promise<void>;
  update: (id: number, payload: RadiologistPatch) => Promise<void>;
  updateMany: (updates: Array<{ id: number; payload: RadiologistPatch }>) => Promise<void>;
  remove: (id: number) => Promise<void>;
  recalculateCapacitiesFromAnalytics: (args: {
    dateFrom: string;
    dateTo: string;
    dateField?: AnalyticsCapacityDateField;
    binMinutes?: BinMinutes;
    /** Recalculate this radiologist only; omitted means every radiologist. */
    radId?: number;
  }) => Promise<RadiologistCapacityRecalcOut[] | undefined>;
};

export const useRadiologistsStore = create<RadiologistsState & RadiologistsActions>((set, get) => ({
  radiologists: {},
  loading: false,

  async list(signal) {
    set({ loading: true, error: undefined });
    try {
      const data = await http<Radiologist[]>("/api/v1/radiologists", {}, signal);
      const normalized = data.map(normalizeRadiologist);
      set({ radiologists: normalized.reduce((acc, r) => ({ ...acc, [r.id]: r }), {}), loading: false });
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      set({ loading: false, error: message });
      toast.error("Failed to load radiologists", { description: message });
    }
  },

  async get(id, signal) {
    try {
      const data = await http<Radiologist>(`/api/v1/radiologists/${id}`, {}, signal);
      const normalized = normalizeRadiologist(data);
      set({ radiologists: { ...get().radiologists, [normalized.id]: normalized } });
      return normalized;
    } catch (err: unknown) {
      if (isAbortError(err)) return;
      toast.error("Failed to get radiologist", { description: getErrorMessage(err) });
    }
  },

  async create(payload) {
    try {
      const data = await http<Radiologist>(`/api/v1/radiologists`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const normalized = normalizeRadiologist(data);
      set({ radiologists: { ...get().radiologists, [normalized.id]: normalized } });
      toast.success("Radiologist created");
      await get().list();
    } catch (err: unknown) {
      toast.error("Failed to create radiologist", { description: getErrorMessage(err) });
    }
  },

  async update(id, payload) {
    try {
      const data = await http<Radiologist>(`/api/v1/radiologists/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      const normalized = normalizeRadiologist(data);
      set({ radiologists: { ...get().radiologists, [normalized.id]: normalized } });
      toast.success("Radiologist updated");
    } catch (err: unknown) {
      toast.error("Failed to update radiologist", { description: getErrorMessage(err) });
    }
  },

  async updateMany(updates) {
    let successCount = 0;
    try {
      // TODO: This is a dumb workaround. Create a batch update endpoint and use that instead.
      for (const { id, payload } of updates) {
        const data = await http<Radiologist>(`/api/v1/radiologists/${id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        const normalized = normalizeRadiologist(data);
        set({ radiologists: { ...get().radiologists, [normalized.id]: normalized } });
        successCount++;
      }
      toast.success("Radiologists updated");
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      if (successCount > 0) {
        toast.warning(`Updated ${successCount}/${updates.length} radiologists`, { description: message });
      } else {
        toast.error("Failed to update radiologists", { description: message });
      }
    }
  },

  async remove(id) {
    try {
      await http<void>(`/api/v1/radiologists/${id}`, { method: "DELETE" });
      toast.success("Radiologist removed");
      set({ radiologists: Object.fromEntries(Object.entries(get().radiologists).filter(([key]) => key !== String(id))) });
    } catch (err: unknown) {
      toast.error("Failed to remove radiologist", { description: getErrorMessage(err) });
    }
  },

  async recalculateCapacitiesFromAnalytics({
    dateFrom,
    dateTo,
    dateField,
    binMinutes = DEFAULT_BIN_MINUTES,
    radId,
  }) {
    const params = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
      bin_minutes: String(binMinutes),
    });
    if (dateField && dateField !== "study_dt") {
      params.set("date_field", dateField);
    }
    if (radId != null) {
      params.set("rad_id", String(radId));
    }
    try {
      const rows = await http<RadiologistCapacityRecalcOut[]>(
        `/api/v1/radiologists/capacity/recalculate-from-analytics?${params}`,
        { method: "POST" },
      );
      await get().list();
      return rows;
    } catch (err: unknown) {
      toast.error("Failed to recalculate capacities", { description: getErrorMessage(err) });
    }
  },
}));
